import { matchPath, generatePath } from "react-router-dom";
import { secureStroge, storgeKeys } from "./";
import { get, invert, keys, findLast, sortBy, find } from "lodash";
import Icon from "@ant-design/icons";
import { ReactComponent as constructionSettingSvg } from "@styles/svgs/construction-setting.svg";
import { ReactComponent as houseSearchSvg } from "@styles/svgs/house-search.svg";
import { ReactComponent as notification } from "@styles/svgs/notification.svg";
import { ReactComponent as news } from "@styles/svgs/news.svg";
import { ReactComponent as searchDeliveryPackage } from "@styles/svgs/package-2.svg";
import { ReactComponent as searchDelegatePackage } from "@styles/svgs/delegate-search.svg";
import { ReactComponent as addVisitor } from "@styles/svgs/add-visitor.svg";
import { ReactComponent as smartLockerGray } from "@styles/svgs/smart-locker-gray.svg";
import { ReactComponent as managementFee } from "@styles/svgs/management-fee-2.svg";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * 功能路由匹配資訊
 * @description 開發新功能時，自動加上對應頁面連結
 */
const routerInfo = {
  CreateConstructionDeliveryPackageByProperty: {
    path: "/DeliveryPackages/Create",
    name: "登記包裹",
    icon: ""
  },
  UpdateConstructionDeliveryPackageByProperty: {
    path: "/DeliveryPackages/Set/:id",
    name: "修改包裹",
    icon: ""
  },
  ChangeConstructionDeliveryPackageToCollectedByProperty: {
    path: "/DeliveryPackages/Collect",
    name: "領取包裹",
    icon: ""
  },
  ConstructionDeliveryPackageManager: {
    path: "/DeliveryPackages",
    name: "包裹管理",
    icon: <Icon component={searchDeliveryPackage} style={{ fontSize: "20px" }} />
  },
  ChangeConstructionDeliveryPackageToWaitReturnByProperty: {
    path: "/DeliveryPackages/Return",
    name: "退貨包裹",
    icon: ""
  },
  ChangeConstructionDeliveryPackageToReturnedByProperty: {
    path: "/DeliveryPackages/Returned",
    name: "退貨完成",
    icon: ""
  },
  ConstructionDelegatePackageManager: {
    path: "/DelegatePackages",
    name: "物品管理",
    icon: <Icon component={searchDelegatePackage} style={{ fontSize: "20px" }} />
  },
  ChangeConstructionDelegatePackageToCollectedByProperty: {
    path: "/DelegatePackages/Collect",
    name: "領取物品",
    icon: ""
  },
  CreateConstructionDelegatePackageByProperty: {
    path: "/DelegatePackages/Create",
    name: "寄放物品",
    icon: ""
  },
  UpdateConstructionDelegatePackageByProperty: {
    path: "/DelegatePackages/Set/:id",
    name: "修改寄放物品",
    icon: ""
  },
  ConstructionNewsManager: {
    path: "/News",
    name: "公告管理",
    icon: <Icon component={news} style={{ fontSize: "20px" }} />
  },
  CreateConstructionNewsByProperty: {
    path: "/News/Create",
    name: "新增社區公告",
    icon: ""
  },
  UpdateConstructionNewsByProperty: {
    path: "/News/Set/:id",
    name: "修改社區公告",
    icon: ""
  },
  ConstructionNotificationManager: {
    path: "/Notifications",
    name: "推播管理",
    icon: <Icon component={notification} style={{ fontSize: "20px" }} />
  },
  HouseholdPropertyManager: {
    path: "/Households",
    name: "住戶管理",
    icon: <Icon component={houseSearchSvg} style={{ fontSize: "20px" }} />
  },
  ViewJoinHouseholdUserByProperty: {
    path: "/Households/Join",
    name: "開通住戶",
    icon: ""
  },
  ConstructionVisitRecordManager: {
    path: "/VisitRecords",
    name: "訪客管理",
    icon: <Icon component={addVisitor} style={{ fontSize: "20px" }} />
  },
  CreateConstructionVisitRecordByProperty: {
    path: "/VisitRecords/Create",
    name: "登記訪客",
    icon: ""
  },
  ConstructionSmartLockerManager: {
    path: "/SmartLockerPackages",
    name: "智慧櫃包裹",
    icon: <Icon component={smartLockerGray} style={{ fontSize: "20px" }} />
  },
  ConstructionPropertyManager: {
    path: "/Settings/Construction",
    name: "建案設定",
    icon: <Icon component={constructionSettingSvg} style={{ fontSize: "20px" }} />
  },
  ConstructionAccessCardManager: {
    path: "/AccessCards",
    name: "門禁卡管理",
    icon: <FontAwesomeIcon icon="credit-card" />
  },
  GetConstructionTargetByProperty: {
    path: "/SearchTarget",
    name: "戶別資訊查詢",
    icon: ""
  },
  // Menu Items
  ConstructionManagementFeeManager: {
    path: "/ManagementFees",
    name: "管理費",
    icon: <Icon component={managementFee} style={{ fontSize: "20px" }} />
  },
  SearchConstructionManagementFeeBillByProperty: {
    path: "/ManagementFees/Bills",
    name: "繳費明細",
    icon: ""
  }
};

const routerMatcher = Object.keys(routerInfo).reduce((acc, key) => {
  acc[key] = routerInfo[key].path;
  return acc;
}, {});

const invertRoutes = invert(routerMatcher);
const sortedInvertRoutes = sortBy(keys(invertRoutes));

export const router = {
  /**
   * 根據索引取得頁面對應位址
   * @param {string} key - 功能權限索引
   * @param {object} params - 網址參數
   * @param {boolean} isFullUrl - 是否回傳完整 uri
   * @description 與 ContentRouter 與 SliderLayout 的頁面連結有關
   */
  getPath(key, params, isFullUrl = false) {
    const prefix = isFullUrl ? window.location.protocol + "//" + window.location.host : "";
    if (key) {
      let path = get(routerMatcher, key);
      if (path) {
        return prefix + (params ? generatePath(path, params) : path);
      }
    }
    return prefix + "/";
  },
  /**
   * 根據頁面實際位址取得索引
   * @param {path} string - 頁面實際位址
   * @description 與 ContentLayout 的麵包屑有關
   */
  getKey(path) {
    const matchRoute = findLast(sortedInvertRoutes, route => {
      return matchPath(path, route);
    });
    return matchRoute ? get(invertRoutes, matchRoute) : null;
  },

  /**
   * 取得模組名稱
   * @param {key} string - 功能權限索引
   * @description 提供各模組取名稱用
   */
  getName(key) {
    const userCredential = secureStroge.get(storgeKeys.userCredential);
    const permission = find(userCredential.permissions, ["key", key]);
    return permission ? routerInfo[key]?.name || permission.name : null;
  },
  /**
   * 取得功能路由資訊
   * @param {string} key - 功能權限索引
   * @returns
   */
  getRouteInfo(key) {
    return routerInfo[key];
  },
  /**
   * 判斷功能路由資訊是否存在
   * @param {string} key - 功能權限索引
   * @returns
   */
  isRouteInfoExist(key) {
    return !!routerInfo[key];
  }
};
